import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

interface StationBreakdownChartProps {
  data: Record<string, unknown>;
}

interface StationEntry {
  name: string;
  time: number;
  isWeak: boolean;
}

const STATION_COLORS = {
  normal: "hsl(150 55% 50%)",
  weak: "hsl(0 70% 55%)",
};

function formatStationTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function StationBreakdownChart({ data }: StationBreakdownChartProps) {
  const chartData = useMemo(() => {
    const stations = data.station_times as Record<string, { latest_value?: number }> | undefined;
    const weakStations = data.weak_stations as Array<{ station?: string }> | undefined;
    const weakSet = new Set((weakStations ?? []).map((w) => w.station?.toLowerCase()));

    if (!stations) return [];
    return Object.entries(stations).map(
      ([name, entry]): StationEntry => ({
        name: name.replace(/_/g, " "),
        time: entry.latest_value ?? 0,
        isWeak: weakSet.has(name.toLowerCase()),
      }),
    );
  }, [data]);

  if (!chartData.length) {
    return (
      <div className="rounded-xl border border-border/60 p-4">
        <h3 className="text-sm font-semibold mb-3">Station Breakdown</h3>
        <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">
          No station data yet. Record Hyrox race times to analyze your stations.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <h3 className="text-sm font-semibold mb-3">Station Breakdown</h3>
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} horizontal={false} />
            <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v: number) => formatStationTime(v)} />
            <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} width={90} />
            <Tooltip
              contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }}
              formatter={(value: number) => [formatStationTime(value), "Time"]}
            />
            <Bar dataKey="time" radius={[0, 4, 4, 0]} maxBarSize={20}>
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.isWeak ? STATION_COLORS.weak : STATION_COLORS.normal} fillOpacity={0.7} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {chartData.some((s) => s.isWeak) && (
        <div className="text-[10px] font-mono text-red-400/80 mt-2">
          ▸ Red bars indicate weak stations needing focused training
        </div>
      )}
    </div>
  );
}
